import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";

export type ThrowMadeEvent = {
  type: "throw_made";
  gameId: string;
  team: 1 | 2;
  outcome: "in_hole" | "on_board" | "miss";
  throwIndex: number;
};

// Subscribes to the SSE stream for a public game share code. Pushes
// frame_added/_undone/game_completed payloads into TanStack Query's
// cache so anything reading useGameByShareCode re-renders
// immediately. Optionally invokes `onThrow` for real-time
// `throw_made` events — used by the multiplayer virtual play page
// to replay the opposite side's bag flight in the renderer.
export function useGameStream(
  shareCode: string | null,
  opts: { onThrow?: (evt: ThrowMadeEvent) => void } = {},
) {
  const qc = useQueryClient();
  const { onThrow } = opts;
  useEffect(() => {
    if (!shareCode) return;
    const url = `/api/g/${shareCode}/stream`;
    const es = new EventSource(url);
    const onGameEvent = (ev: MessageEvent) => {
      try {
        const data = JSON.parse(ev.data);
        if (data?.gameState) {
          qc.setQueryData(["g", shareCode], data.gameState);
        }
      } catch {
        // ignore malformed frames
      }
    };
    const onThrowEvent = (ev: MessageEvent) => {
      try {
        const data = JSON.parse(ev.data) as ThrowMadeEvent;
        onThrow?.(data);
      } catch {
        // ignore malformed throws — better to drop one bag than
        // break the whole game's stream
      }
    };
    es.addEventListener("frame_added", onGameEvent);
    es.addEventListener("frame_undone", onGameEvent);
    es.addEventListener("game_completed", onGameEvent);
    es.addEventListener("throw_made", onThrowEvent);
    es.addEventListener("game_voided", () => {
      qc.invalidateQueries({ queryKey: ["g", shareCode] });
    });
    es.onerror = () => {
      // Browser auto-reconnects on its own; nothing to do.
    };
    return () => {
      es.removeEventListener("frame_added", onGameEvent);
      es.removeEventListener("frame_undone", onGameEvent);
      es.removeEventListener("game_completed", onGameEvent);
      es.removeEventListener("throw_made", onThrowEvent);
      es.close();
    };
  }, [shareCode, qc, onThrow]);
}
